import { useEffect, useState } from 'react';
import { useUser } from '../contexts/user-profile';
import {
  getVariant,
  onVariantsChanged,
  startExperiment,
} from './experiment-client';
import { FLAG_ON, FeatureFlagKey } from './flags';

function isOn(flagKey: FeatureFlagKey): boolean {
  return getVariant(flagKey) === FLAG_ON;
}

/**
 * Whether a feature flag is on for the current user.
 *
 * Reads what is already known at mount rather than starting at `false`: with
 * variants fetched during app init, a gated surface renders its final state
 * first time instead of painting the off branch and swapping a moment later.
 * It then re-reads on every refresh, which is how turning a flag off reaches a
 * tab that is already open.
 *
 * Returns `false` for every flag when Experiment has no deployment key, which
 * is how local, airgapped and not-yet-configured environments behave.
 */
export function useFeatureFlag(flagKey: FeatureFlagKey): boolean {
  const user = useUser();
  const userId = user?.email;
  const [enabled, setEnabled] = useState(() => isOn(flagKey));

  useEffect(() => {
    if (!userId) {
      return;
    }
    let active = true;
    const sync = () => {
      if (active) {
        setEnabled(isOn(flagKey));
      }
    };
    startExperiment(userId).then(sync);
    const unsubscribe = onVariantsChanged(sync);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [flagKey, userId]);

  return enabled;
}
